import {
  MessageBoxVariant,
  MessageBoxWidgetDto,
  ResolvedMessageBoxDeviceConfig,
} from "@intelligems/ig-types";
import { merge } from "@intelligems/ig-utils";
import {
  resolveDesktopMessageBoxConfig,
  resolveMobileMessageBoxConfig,
} from "@intelligems/ig-widgets/MessageBoxView/resolveMessageBoxConfig";
import { createSelector } from "@reduxjs/toolkit";
import { RootState, useSelectorFactory } from "@src/store";
import { getMessageBoxInterfaceForVariation } from "@src/store/slices/userInterfaces/selectors";
import { getMessageBox, selectedWidgets } from "@src/store/slices/widgets";
import { MessageBoxConfig } from "@src/store/slices/widgets/types";
import { filterWidgets } from "@src/store/slices/widgets/util";

export const getLibraryMessageBoxes = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<MessageBoxWidgetDto>(widgets, {
      messageBoxPopup: true,
      messageBoxSlideOut: true,
    }).map(mergeWidget)
);

export const getLibraryMessageBoxPopups = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<MessageBoxWidgetDto>(widgets, {
      messageBoxPopup: true,
    }).map(mergeWidget)
);

export const getMessageBoxSlideOuts = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<MessageBoxWidgetDto>(widgets, { messageBoxSlideOut: true })
);

export const getLibraryMessageBoxSlideOuts = createSelector(
  getMessageBoxSlideOuts,
  (widgets) => widgets.map(mergeWidget)
);

export const getLibraryDrawerMessageBox = createSelector(
  getMessageBox,
  (widget) => (widget ? mergeWidget(widget) : undefined)
);

// combines interface widget with user interface variables
// and sets defaults for preview
export const getUserInterfaceMessageBox = (variationId: string) =>
  createSelector(
    getMessageBoxInterfaceForVariation(variationId),
    (userInterface) => {
      if (!userInterface?.widget) return undefined;

      const desktopStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.desktop?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.desktop?.styles;

      const mobileStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.mobile?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.mobile?.styles;

      return merge(userInterface.widget, {
        config: {
          desktop: {
            variables: userInterface.desktopVariables,
            styles: desktopStyles,
          },
          mobile: {
            variables: userInterface.mobileVariables,
            styles: mobileStyles,
          },
        },
      });
    }
  );
export const useUserInterfaceMessageBox = (variationId: string) =>
  useSelectorFactory(getUserInterfaceMessageBox, variationId);

// combines drawer widget with user interface variables
// and sets defaults for preview
export const getUserInterfaceDrawerMessageBox = (variationId: string) =>
  createSelector(
    getMessageBox,
    getMessageBoxInterfaceForVariation(variationId),
    (widget, userInterface) => {
      if (!widget || !userInterface) return undefined;

      return merge(widget, (merger) => {
        merger.merge({
          config: {
            desktop: {
              variables: userInterface.desktopVariables,
            },
            mobile: {
              variables: userInterface.mobileVariables,
            },
          },
        });
        merger.merge(getDefaultMessageBoxWidget(), (original, value) =>
          !original ? value : original
        );
      });
    }
  );
export const useUserInterfaceDrawerMessageBox = (variationId: string) =>
  useSelectorFactory(getUserInterfaceDrawerMessageBox, variationId);

export const isMessageBoxPopupEnabled =
  (_variationId: string) => (_state: RootState) =>
    true;

export const isMessageBoxSlideOutEnabled =
  (_variationId: string) => (_state: RootState) =>
    true;

export const getResolvedDesktopMessageBoxConfig = createSelector(
  getMessageBox,
  (widget) => {
    if (widget === undefined) return undefined;
    return resolveDesktopMessageBoxConfig(widget.config);
  }
);

export const getResolvedMobileMessageBoxConfig = createSelector(
  getMessageBox,
  (widget) => {
    if (widget === undefined) return undefined;
    return resolveMobileMessageBoxConfig(widget.config);
  }
);

export const getDesktopMessageBoxConfig = createSelector(
  getMessageBox,
  getResolvedDesktopMessageBoxConfig,
  (widget, config) =>
    getMessageBoxConfig(widget, config, "messageBoxPopupWidget1")
);

export const getMobileMessageBoxConfig = createSelector(
  getMessageBox,
  getResolvedMobileMessageBoxConfig,
  (widget, config) =>
    getMessageBoxConfig(widget, config, "messageBoxPopupWidget1")
);

export const areDeviceMessageBoxDifferent = createSelector(
  getResolvedDesktopMessageBoxConfig,
  getResolvedMobileMessageBoxConfig,
  (desktop, mobile) =>
    JSON.stringify({
      variant: desktop?.variant,
      variables: desktop?.variables,
      styles: desktop?.styles,
    }) !==
    JSON.stringify({
      variant: mobile?.variant,
      variables: mobile?.variables,
      styles: mobile?.styles,
    })
);

function getMessageBoxConfig(
  widget: MessageBoxWidgetDto | undefined,
  config: ResolvedMessageBoxDeviceConfig | undefined,
  defaultVariant: MessageBoxVariant
): MessageBoxConfig | undefined {
  if (widget === undefined || config === undefined) return undefined;

  const styles = config.styles;
  return {
    general: {
      name: {
        value: widget.name,
        path: "name",
      },
      variant: {
        value: config.variant ?? defaultVariant,
        path: "variant",
      },
    },
    colors: {
      textColor: {
        value: styles.container.textColor,
        path: "styles.container.textColor",
      },
      backgroundColor: {
        value: styles.container.backgroundColor,
        path: "styles.container.backgroundColor",
      },
    },
  };
}

function mergeWidget(widget: MessageBoxWidgetDto) {
  return merge(widget, (merger) => {
    merger.merge(getDefaultMessageBoxWidget(), (original, value) =>
      !original ? value : original
    );
  });
}

function getDefaultMessageBoxDevice() {
  return {
    variables: {
      textReplacements: {
        title: "PROMO TITLE HERE!",
        body: "Sit amet, consectetur adipiscin elit, sed do eiusmod tempor. Ut enim ad minim.",
      },
    },
  };
}

function getDefaultMessageBoxWidget() {
  return {
    config: {
      desktop: getDefaultMessageBoxDevice(),
      mobile: getDefaultMessageBoxDevice(),
    },
  };
}
